import api from './api';
import { Trade, Order, Alert, Instrument, ApiResponse, PaginatedResponse } from '../types';

export const tradingService = {
  // Alerts
  async getAlerts(params?: any) {
    return api.get<ApiResponse<PaginatedResponse<Alert>>>('/alerts', { params });
  },

  async createAlert(alert: Partial<Alert>) {
    return api.post<ApiResponse<Alert>>('/alerts', alert);
  },

  /** Manual price alert: notify when SYMBOL goes above/below `price`. */
  async createPriceAlert(p: { symbol: string; exchange: string; comparator: 'above' | 'below'; price: number }) {
    return api.post<ApiResponse<Alert>>('/alerts/price', p);
  },

  async acknowledgeAlert(id: number) {
    return api.put(`/alerts/${id}/acknowledge`);
  },

  // Test endpoint for debugging order placement
  async testPlaceOrderFromAlert(alertId: number) {
    return api.post(`/test/place-order-from-alert`, { alertId });
  },

  // Orders
  async getOrders(params?: any) {
    return api.get<ApiResponse<PaginatedResponse<Order>>>('/orders', { params });
  },

  async createOrder(order: Partial<Order>) {
    return api.post<ApiResponse<Order>>('/orders', order);
  },

  async cancelOrder(id: number) {
    return api.delete(`/orders/${id}`);
  },

  async modifyOrder(id: number, updates: Partial<Order>) {
    return api.put<ApiResponse<Order>>(`/orders/${id}`, updates);
  },

  // Trades
  async getTrades(params?: any) {
    return api.get<ApiResponse<PaginatedResponse<Trade>>>('/trades', { params });
  },

  async getTradeDetails(id: number) {
    return api.get<ApiResponse<Trade>>(`/trades/${id}`);
  },

  async closeTrade(id: number, params: any) {
    return api.post(`/trades/${id}/close`, params);
  },

  async addToPosition(id: number, params: any) {
    return api.post(`/trades/${id}/add`, params);
  },

  async syncPositions() {
    // Suppress the global error toast — the dashboard's syncPositionsWithRetry
    // handles IP-whitelist + transient broker errors locally and surfaces
    // them via the IP-whitelist banner / dedicated toast. Without this every
    // mount-time sync that times out or fails fires a duplicate popup.
    return api.post('/trades/sync', undefined, { headers: { _suppressToast: 'true' } });
  },

  async squareOffAll(params?: { side?: string }) {
    const response = await api.post('/trades/square-off', params || {});
    return response.data;
  },

  async executeAlert(alertId: number) {
    const response = await api.post(`/alerts/${alertId}/execute`);
    return response.data;
  },

  async parkAlert(alertId: number) {
    const response = await api.put(`/alerts/${alertId}/park`);
    return response.data;
  },

  async dismissAlert(alertId: number) {
    const response = await api.put(`/alerts/${alertId}/dismiss`);
    return response.data;
  },

  async setTarget(tradeId: number, target: number) {
    const response = await api.put(`/trades/${tradeId}/target`, { target });
    return response.data;
  },

  async deleteTrade(id: number) {
    return api.delete(`/trades/${id}`);
  },

  async createManualTrade(data: {
    symbol: string; exchange: string; side: string;
    quantity: number; entryPrice: number;
    stopLoss?: number; target?: number;
    tradeType?: string; productType?: string; notes?: string;
  }) {
    const response = await api.post('/trades/manual', data);
    return response.data;
  },

  async cancelDraftTrade(id: number) {
    const response = await api.post(`/trades/${id}/cancel-draft`);
    return response.data;
  },

  async resolveLimboTrade(id: number, closePrice: number) {
    const response = await api.post(`/trades/${id}/resolve-limbo`, { closePrice });
    return response.data;
  },

  // Instruments
  async getInstruments(params?: any) {
    return api.get<ApiResponse<PaginatedResponse<Instrument>>>('/instruments', { params });
  },

  async setManualStopLoss(tradeId: number, manualStopLoss: number) {
    const response = await api.put(`/trades/${tradeId}/stop-loss`, { manualStopLoss });
    return response.data;
  },

  async updateInstrument(id: number, updates: Partial<Instrument>) {
    return api.put<ApiResponse<Instrument>>(`/instruments/${id}`, updates);
  },

  async getSystemPerformance(days: number) {
    // Background poll on Dashboard — local fallback shows skeleton/empty state on failure.
    return api.get<any>('/dashboard/performance', { params: { days }, headers: { _suppressToast: 'true' } });
  }
};
